export default {
  name: {
    id: 'rm_metal_silicon_name',
    defaultMessage: 'Silicon',
  },
  smartphoneLabel: {
    id: 'rm_metal_silicon_smartphone_label',
    defaultMessage: 'Major',
  },
  whatItDoesText: {
    id: 'rm_metal_silicon_what_it_does',
    defaultMessage:
      'Silicon is the skeleton of the modern chip, the base material on which every transistor, every processor, and every memory cell is built. It also forms the light-sensing surface of the camera and is a key ingredient of the screen glass.',
  },
  extractionText1: {
    id: 'rm_metal_silicon_extraction_p1',
    defaultMessage:
      "Quartz rock is mined, crushed, and fed into electric arc furnaces with carbon-based materials, which strip the oxygen and leave raw silicon metal. To reach chip-grade purity, the silicon is then converted into a gas, purified by distillation, and re-solidified into a perfect single crystal, the ingot from which wafers are cut. This gas-phase purification step alone accounts for 64% of all electricity used across silicon's production chain.",
  },
  quickFactText: {
    id: 'rm_metal_silicon_quick_fact',
    defaultMessage:
      "32 litres of water is required to make one 2-gram silicon chip. It represents 16,000 times the chip's own weight.",
  },
  envSummaryText: {
    id: 'rm_metal_silicon_env_summary',
    defaultMessage:
      "Silicon is everywhere, making up 28% of the Earth's crust. And yet turning it into something usable costs a lot. The reason is energy. Refining quartz into chip-grade silicon demands extreme temperatures and multiple energy-intensive purification steps. Of all the minerals studied in the scientific literature, silicon has one of the largest climate change impact per tonne produced.",
  },
  envWaterText: {
    id: 'rm_metal_silicon_env_water_text',
    defaultMessage:
      "Making chips from silicon requires not just large volumes of water, but exceptionally pure water, which must itself be produced through energy-intensive treatment. In 2021, a severe drought in Taiwan, home to the world's most advanced chip factories, disrupted global electronics production and reduced water available for local agriculture. As water stress increases in key production regions, the competition between chip factories and nearby communities for scarce freshwater will only intensify.",
  },
  envSoilText: {
    id: 'rm_metal_silicon_env_soil_text',
    defaultMessage:
      "Quartz mining is open-cast at scale: rock is blasted, land is stripped, and large quantities of waste rock are left behind. Because much of the world's quartz mining is illegal or unregulated, happening across more than 70 countries, proper land rehabilitation is rarely carried out.",
  },
  envClimateText: {
    id: 'rm_metal_silicon_env_climate_text',
    defaultMessage:
      "Silicon's climate footprint is driven by energy. Producing chip-grade silicon requires smelting at extreme temperatures; one purification step alone consumes 150 megawatt-hours per tonne, over 64% of the total energy in the production chain. As the best quartz deposits run out, producers turn to lower-grade material, which needs more energy to refine. With much of quartz mining illegal and unrecorded, the true climate cost is almost certainly higher than current estimates suggest.",
  },
  humanSummaryText: {
    id: 'rm_metal_silicon_human_summary',
    defaultMessage:
      'Silicon metal is produced almost entirely in China, where production has expanded rapidly, including in Xinjiang. The available evidence on human impacts concerns mainly silica dust exposure during the mining and processing of quartz and other silica-rich rock, not silicon metal itself. These fine particles are invisible to the naked eye and can become lodged deep in the lungs. The resulting damage can be slow, irreversible and fatal.',
  },
  humanWaterText: {
    id: 'rm_metal_silicon_human_water_text',
    defaultMessage:
      'The factories that turn silicon wafers into the chips inside your phone are extremely water-intensive. The world’s most advanced chip production is concentrated in Taiwan, where severe droughts in 2021 disrupted semiconductor manufacturing and reduced the water available for agriculture across the island. As water stress worsens in regions where chips are made, competition between industrial production and local communities for the same finite supplies is likely to grow.',
  },
  humanHealthText: {
    id: 'rm_metal_silicon_human_health_text',
    defaultMessage:
      'Mining and processing silica-rich rock releases silica dust, fine airborne particles generated when rock is blasted, crushed and processed. When inhaled, these particles can lodge deep in the lungs and cause silicosis, a progressive and irreversible disease for which there is no cure. Silica exposure is also associated with lung cancer, tuberculosis and cardiovascular disease. A 2012 study of workers in Chinese metal mines and pottery factories found higher all-cause mortality among those exposed to silica dust.',
  },
  humanLegalText: {
    id: 'rm_metal_silicon_human_legal_text',
    defaultMessage:
      'Much of global quartz mining is illegal, taking place in more than 70 countries with little official oversight. Injuries, illnesses and deaths among workers may go undocumented. Silicosis, the lung disease caused by silica dust, can take decades to develop, long after employment has ended, making access to remedy difficult. Quartz and silica-rich rock are among the world’s most illegally traded raw materials, yet the human cost of this trade leaves almost no trace.',
  },
  geoSummaryText: {
    id: 'rm_metal_silicon_geo_summary',
    defaultMessage:
      'China dominates the silicon supply chain, especially at the processing and manufacturing stages. Silicon metal cannot be mined directly: it is produced by reducing mined quartz, quartzite or silica sand in electric arc furnaces. China produces around 85% of the world’s silicon metal, 95% of its monocrystalline silicon and 97% of the wafers used in chips and solar panels. Concentration increases at each stage of the production chain, leaving phone, solar-panel and battery manufacturers dependent on a dominant supplier.',
  },
  geoDugText: {
    id: 'rm_metal_silicon_geo_dug_text',
    defaultMessage:
      'Silicon metal is not mined. It is produced from mined quartz, quartzite or silica sand. China produces around 85% of the world’s silicon metal, while Brazil, Norway, France and Germany account for much of the remainder.',
  },
  geoProcessedText: {
    id: 'rm_metal_silicon_geo_processed_text',
    defaultMessage:
      'China produces 95% of the world’s monocrystalline silicon, the high-purity, single-crystal form used to make the wafers that carry transistors in smartphone chips and other electronics, as well as solar cells.',
  },
  geoLeverageText: {
    id: 'rm_metal_silicon_geo_leverage_text',
    defaultMessage:
      'China applies non-automatic export licensing to silicon, meaning that individual shipments require government approval. Combined with its dominant position across the production chain, this gives China significant leverage over global supply without requiring an outright export ban.',
  },
  supplySummaryText: {
    id: 'rm_metal_silicon_supply_summary',
    defaultMessage:
      'Supply concentration risks are greater at the refining stage than at the raw-material stage. Silicon is the second-most abundant element in the Earth’s crust, but silicon metal cannot be mined directly. It is produced from high-purity quartzite or silica through specialised refining processes. China produces around 70% to 80% of global silicon metal. At the high-purity stage, concentration is even greater: in 2024, global production capacity reached 1,900 kt, with 95% of production coming from China, followed by Germany (50 kt), Malaysia (23 kt) and the United States (16 kt). As solar PV expands, this geographic concentration creates supply-chain risk.',
  },
  supplyReservesText: {
    id: 'rm_metal_silicon_supply_reserves_text',
    defaultMessage:
      'No official global estimate of silicon reserves exists. GreenIT projects that economically recoverable reserves of silicon metal could be exhausted around 2040. This is a snapshot based on current knowledge, extraction rates and projected demand, not a fixed geological deadline. Reserves can change with new discoveries, technology, prices and demand.',
  },
  supplyDemandText: {
    id: 'rm_metal_silicon_supply_demand_text',
    defaultMessage:
      'Demand for silicon in clean-energy technologies is projected to increase by around 20%, from 1.705 million tonnes in 2025 to 2.038 million tonnes in 2050. Solar PV dominates demand today, accounting for 99% of the total, but its share is expected to fall to 65% by 2050. Meanwhile, EV batteries become a major source of growth, reaching around 34% of clean-energy demand as silicon-anode batteries scale.',
  },
  recyclingSummaryText: {
    id: 'rm_metal_silicon_recycling_summary',
    defaultMessage:
      'Silicon is essentially unrecycled from electronic devices. During the recycling of phones, laptops and other digital devices, silicon is generally not recovered, and no industrial-scale recovery operation exists anywhere in the world. In solar-panel recycling, recovery is technically feasible and pilot projects are underway, but no large-scale commercial process has yet been established.',
  },
  recyclingInputText: {
    id: 'rm_metal_silicon_recycling_input_text',
    defaultMessage:
      'Effectively 0% of the silicon used in new electronic devices comes from recycled sources. Current supply comes from newly produced silicon made from mined quartz or silica.',
  },
  recyclingOutlookText: {
    id: 'rm_metal_silicon_recycling_outlook_text',
    defaultMessage:
      'Recycling silicon from chips is not currently practised at industrial scale anywhere. In solar-panel recycling, recovery efforts are underway, but no large-scale commercial process has yet been established. Industrial-scale recycling of silicon from electronic devices remains undeveloped.',
  },
  footnote1Text1: {
    id: 'rm_metal_silicon_footnote_1_p1',
    defaultMessage:
      'These data refer to silicon metal production, not mining or mine output. Silicon metal is not extracted from dedicated “silicon mines.” Instead, it is produced by reducing high-purity quartz, quartzite or silica sand with carbon in electric arc furnaces. Major silicon-producing countries source these raw materials from large-scale quartz and silica operations.',
  },
  footnote1Text2: {
    id: 'rm_metal_silicon_footnote_1_p2',
    defaultMessage:
      'Silicon metal is the generally accepted trading name for elemental silicon refined in its metalloid form, covering both metallurgical grade and higher purity grades',
  },
  footnote2Text1: {
    id: 'rm_metal_silicon_footnote_2_p1',
    defaultMessage:
      'This score was not assessed by ADEME. We estimate its impact at 3/5 based only on climate impact: the PBL identifies silicon production as having the highest climate impact per tonne among the materials assessed. Producing high-purity silicon requires large amounts of energy, and current assessments may underestimate emissions because parts of the supply chain remain difficult to trace. [PBL 2024].',
  },
  mine0Region: {
    id: 'rm_metal_silicon_mine0_region',
    defaultMessage: 'Leshan',
  },
  mine1Region: {
    id: 'rm_metal_silicon_mine1_region',
    defaultMessage: 'Shihezi',
  },
  mine2Region: {
    id: 'rm_metal_silicon_mine2_region',
    defaultMessage: 'Burghausen',
  },
  mine3Region: {
    id: 'rm_metal_silicon_mine3_region',
    defaultMessage: 'Alloy',
  },
  mine4Region: {
    id: 'rm_metal_silicon_mine4_region',
    defaultMessage: 'Xuzhou',
  },
} as const
